import React, { useState, useEffect, useCallback, useMemo } from "react";
import {getMenu, createMenu, getPermissions,updateMenu, getMenuById, getAllMenuMaster,clearMenuCache} from "./services/masterDataService";
import Toast from "./context/ToastContext";
import { Search, RotateCcw, Plus, Edit2, ChevronUp, ChevronDown  } from 'lucide-react';
import { FaToggleOn, FaToggleOff } from "react-icons/fa";

const COLORS = {
  bg: "#f1f5f9",
  surface: "#ffffff",
  border: "#e2e8f0",
  accent: "#0aa375",
  accentMuted: "#f0fdfa",
  MuneSelected: "#d8fdf5",
  danger: "#ef4444",
  text: "#1e293b",
  textMuted: "#64748b",
  headerBg: "#f8fafc",
  shadow: "0 4px 6px -1px rgba(0,0,0,0.07)",
};

const BRAND_GREEN = '#0aa375';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
  body, html { margin: 0; padding: 0; overflow: hidden; height: 100%; font-family: 'Plus Jakarta Sans', sans-serif; background-color: ${COLORS.bg}; color: ${COLORS.text}; }
  .glass-card { background: #fff; border: 1px solid ${COLORS.border}; box-shadow: ${COLORS.shadow}; border-radius: 10px; }
  .tree-row { transition: background 0.15s; border-bottom: 1px solid #f1f5f9; }
  .tree-row:hover { background-color: #f8fffe !important; box-shadow: inset 3px 0 0 ${COLORS.accent}; }
  .btn-primary { background: ${COLORS.accent}; color: white; border: none; padding: 7px 18px; border-radius: 7px; font-weight: 600; font-size: 12px; cursor: pointer; }
  .btn-primary:hover { opacity: 0.9; }
  .switch { position: relative; display: inline-block; width: 36px; height: 19px; }
  .switch input { opacity: 0; width: 0; height: 0; }
  .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #cbd5e1; transition: .3s; border-radius: 20px; }
  .slider:before { position: absolute; content: ""; height: 13px; width: 13px; left: 3px; bottom: 3px; background-color: white; transition: .3s; border-radius: 50%; }
  input:checked + .slider { background-color: ${COLORS.accent}; }
  input:checked + .slider:before { transform: translateX(17px); }
  .custom-scroll::-webkit-scrollbar { width: 5px; }
  .custom-scroll::-webkit-scrollbar-track { background: transparent; }
  .custom-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
  .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(15,23,42,0.6); display: flex; align-items: center; justify-content: center; z-index: 1000; backdrop-filter: blur(4px); }
  .perm-badge { background: ${COLORS.accentMuted}; color: ${COLORS.accent}; padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; border: 1px solid ${COLORS.accent}22; display: inline-block; }
  input[type="text"], input[type="number"], select { outline: none; border: 1px solid ${COLORS.border} !important; transition: border-color 0.2s; }
  input:focus, select:focus { border-color: ${COLORS.accent} !important; box-shadow: 0 0 0 2px ${COLORS.accent}18; }
`;

const buildMenuTree = (list) => {
  const map = {};
  const roots = [];
  list.forEach((item) => { map[item.menuId] = { ...item, children: [] }; });
  list.forEach((item) => {
    const node = map[item.menuId];
    if (item.parentId !== 0 && map[item.parentId]) map[item.parentId].children.push(node);
    else roots.push(node);
  });
  return roots.sort((a, b) => a.seq - b.seq);
};

const getNextSeq = (menus, parentId) => {
  const siblings = menus.filter(m => (m.parentId || 0) === (parseInt(parentId) || 0));
  if (siblings.length === 0) return 1;
  const maxSeq = Math.max(...siblings.map(m => m.seq || 0));
  return maxSeq + 1;
};

const SearchableSelect = ({ value, onChange, options, placeholder = "Select parent..." }) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const triggerRef = React.useRef(null);
  const dropdownRef = React.useRef(null);

  const selected = options.find(o => String(o.value) === String(value));

  const filtered = options.filter(o =>
    o.label.toLowerCase().includes(query.toLowerCase())
  );

  React.useEffect(() => {
    if (open && triggerRef.current && dropdownRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      dropdownRef.current.style.top = `${rect.bottom + 4}px`;
      dropdownRef.current.style.left = `${rect.left}px`;
      dropdownRef.current.style.width = `${rect.width}px`;
    }
  }, [open]);

  React.useEffect(() => {
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setOpen(false);
    setQuery("");
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      {/* Trigger */}
      <div
        ref={triggerRef}
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", padding: "6px 8px", borderRadius: "6px",
          border: `1px solid ${open ? COLORS.accent : COLORS.border}`,
          fontSize: "12px", boxSizing: "border-box", background: "#fff",
          cursor: "pointer", color: selected ? COLORS.text : COLORS.textMuted,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          boxShadow: open ? `0 0 0 2px ${COLORS.accent}18` : "none",
          transition: "border-color 0.2s"
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.label : placeholder}
        </span>
        <span style={{ fontSize: "10px", color: COLORS.textMuted, marginLeft: "6px", flexShrink: 0 }}>
          {open ? "▲" : "▼"}
        </span>
      </div>
      {open && (
        <div
          ref={dropdownRef}
          style={{
            position: "fixed",  
            background: "#fff",
            border: `1px solid ${COLORS.border}`,
            borderRadius: "6px",
            zIndex: 9999,  
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            overflow: "hidden"
          }}
        >
          <div style={{ padding: "6px", borderBottom: `1px solid ${COLORS.border}` }}>
            <input
              autoFocus
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Type to search..."
              style={{
                width: "100%", padding: "5px 8px", borderRadius: "4px",
                border: `1px solid ${COLORS.border}`, fontSize: "12px",
                boxSizing: "border-box", outline: "none"
              }}
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div style={{ maxHeight: "180px", overflowY: "auto" }}>
            {filtered.length > 0 ? (
              filtered.map(o => (
                <div
                  key={o.value}
                  onMouseDown={e => e.preventDefault()} 
                  onClick={() => handleSelect(o.value)}
                  style={{
                    padding: "7px 10px", fontSize: "12px", cursor: "pointer",
                    background: String(o.value) === String(value) ? COLORS.accentMuted : "#fff",
                    color: String(o.value) === String(value) ? COLORS.accent : COLORS.text,
                    fontWeight: String(o.value) === String(value) ? "600" : "400",
                  }}
                  onMouseEnter={e => { if (String(o.value) !== String(value)) e.currentTarget.style.background = COLORS.bg; }}
                  onMouseLeave={e => { if (String(o.value) !== String(value)) e.currentTarget.style.background = "#fff"; }}
                >
                  {o.label}
                </div>
              ))
            ) : (
              <div style={{ padding: "10px", fontSize: "12px", color: COLORS.textMuted, textAlign: "center" }}>
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const MenuFormModal = ({ show, onClose, onSave, saving, form, setForm, selectedPerms, setSelectedPerms, availablePermissions, parentOptions, editingId, tableMenus }) => {
  if (!show) return null;

  const inp = { width: "100%", padding: "7px 10px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "13px", boxSizing: "border-box", background: "#fff" };
  const lbl = { fontSize: "10.5px", fontWeight: "600", color: COLORS.textMuted, marginBottom: "4px", display: "block", textTransform: "uppercase", letterSpacing: "0.4px" };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="glass-card"
        onClick={e => e.stopPropagation()}
        style={{ width: "620px", maxHeight: "88vh", overflowY: "auto", borderRadius: "12px", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderBottom: `1px solid ${COLORS.border}`, background: COLORS.accent, borderRadius: "12px 12px 0 0" }}>
          <span style={{ fontWeight: "700", fontSize: "15px", color: "#fff" }}>
            {editingId ? "Edit Menu" : "Add New Menu"}
          </span>
          <button 
            onClick={onClose} 
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: "26px", color: "#fff", opacity: 0.9, lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: "18px 20px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
            <div>
              <label style={lbl}>Menu Name <span style={{ color: COLORS.danger }}>*</span></label>
              <input
                disabled={!!editingId}
                type="text"
                value={form.menuName}
                onChange={e => setForm({ ...form, menuName: e.target.value })}
                style={{ ...inp, background: editingId ? "#f8fafc" : "#fff", color: editingId ? COLORS.textMuted : COLORS.text }}
                placeholder="e.g. UserManagement"
              />
            </div>
            <div>
              <label style={lbl}>Display Name <span style={{ color: COLORS.danger }}>*</span></label>
              <input
                type="text"
                value={form.displayName}
                onChange={e => setForm({ ...form, displayName: e.target.value })}
                style={inp}
                placeholder="e.g. User Management"
              />
            </div>
            <div>
              <label style={lbl}>Parent Menu</label>
              <SearchableSelect
                value={form.parentId}
                onChange={val => {
                  if (editingId) {
                    setForm({ ...form, parentId: val });
                  } else {
                    const nextSeq = getNextSeq(tableMenus, val);
                    setForm({ ...form, parentId: val, seq: nextSeq });
                  }
                }}
                options={parentOptions}
                placeholder="Select parent..."
              />
            </div>
            <div>
              <label style={lbl}>Routing Path</label>
              <input
                type="text"
                value={form.routingPath}
                onChange={e => setForm({ ...form, routingPath: e.target.value })}
                style={inp}
                placeholder="e.g. /admin/users"
              />
            </div>
            <div>
              <label style={lbl}>Sequence</label>
              <input 
                type="number" 
                value={form.seq} 
                onChange={e => setForm({ ...form, seq: e.target.value })} 
                style={inp} 
                min="1" 
              />
            </div>
            <div>
              <label style={lbl}>Status</label>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", height: "36px" }}>
                <label className="switch">
                  <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />
                  <span className="slider"></span>
                </label>
                <span style={{ fontSize: "12.5px", fontWeight: "600", color: form.isActive ? COLORS.accent : COLORS.textMuted }}>
                  {form.isActive ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </div>
          <div>
            <label style={{ ...lbl, marginBottom: "8px" }}>Permissions</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", padding: "12px", background: COLORS.bg, borderRadius: "8px", border: `1px solid ${COLORS.border}` }}>
              {availablePermissions.map(p => {
                const isChecked = selectedPerms.has(p.permissionId);
                return (
                  <div
                    key={p.permissionId}
                    onClick={() => { 
                      const n = new Set(selectedPerms); 
                      isChecked ? n.delete(p.permissionId) : n.add(p.permissionId); 
                      setSelectedPerms(n); 
                    }}
                    style={{ padding: "5px 11px", border: `1px solid ${isChecked ? COLORS.accent : COLORS.border}`, borderRadius: "6px", cursor: "pointer", background: isChecked ? COLORS.accentMuted : "#fff", fontSize: "11.5px", color: isChecked ? COLORS.accent : COLORS.textMuted, fontWeight: isChecked ? "600" : "400", transition: "all 0.15s"  }}
                  >
                    {isChecked ? "✓ " : ""}{p.name}
                  </div>
                );
              })}
            </div>
            {selectedPerms.size > 0 && (
              <div style={{ fontSize: "11px", color: COLORS.textMuted, marginTop: "6px" }}>
                {selectedPerms.size} permission(s) selected
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", padding: "12px 20px", borderTop: `1px solid ${COLORS.border}`, background: COLORS.headerBg, borderRadius: "0 0 12px 12px" }}>
          <button
            onClick={onClose}
            style={{ padding: "7px 18px", borderRadius: "7px", border: `1px solid ${COLORS.border}`, background: "#fff", fontSize: "12.5px", fontWeight: "600", cursor: "pointer", color: COLORS.text }}
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="btn-primary"
            style={{ minWidth: "90px", padding: "7px 20px" }}
          >
            {saving ? "Saving..." : (editingId ? "Update" : "Save")}
          </button>
        </div>
      </div>
    </div>
  );
};
const TreeRow = ({ node, onEdit, onDelete, onShowPerms, activeId, openIds, onToggle, level = 0, availablePermissions, allMenus }) => {
  const hasChildren = node.children && node.children.length > 0;
  const isOpen = openIds.has(node.menuId);
  const isSelected = activeId === node.menuId;
  const parentMenu = allMenus.find(m => m.menuId === node.parentId);
  const parentName = parentMenu ? parentMenu.menuName : "—";
  const permissionCount = node.permissionIds?.length || 0;

  return (
    <>
      <div
        className="tree-row"
        onClick={() => onEdit(node.menuId)}
        style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 0.8fr 1fr 1.8fr 110px", padding: "4px 16px", alignItems: "center", cursor: "pointer", background: isSelected ? COLORS.MuneSelected : "transparent", borderLeft: isSelected ? `3px solid ${COLORS.accent}` : "3px solid transparent", fontSize: "12.5px", transition: "all 0.15s" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: level * 20 }}>
          <span
            onClick={(e) => { e.stopPropagation(); onToggle(node.menuId); }}
            style={{ cursor: "pointer", width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '3px', background: hasChildren ? '#f1f5f9' : 'transparent', color: COLORS.textMuted, fontSize: '9px', flexShrink: 0 }}
          >
            {hasChildren ? (isOpen ? "▼" : "▶") : "•"}
          </span>
          <span style={{ fontWeight: hasChildren ? "600" : "400", color: isSelected ? COLORS.accent : COLORS.text }}>{node.menuName}</span>
        </div>
        <div style={{ color: COLORS.textMuted, fontSize: '12px' }}>{node.displayName}</div>
        <div>
          <span style={{ fontSize: "11px", color: COLORS.accent, fontWeight: "600", background: COLORS.accentMuted, padding: '2px 6px', borderRadius: '4px' }}>{parentName}</span>
        </div>
        <div style={{ fontSize: "11px", color: COLORS.textMuted, fontFamily: 'monospace' }}>{node.routingPath || "—"}</div>
        <div
          onClick={(e) => { e.stopPropagation(); permissionCount > 0 && onShowPerms(node); }}
          style={{ fontSize: "12px", color: permissionCount > 0 ? COLORS.accent : '#cbd5e1', fontWeight: "600", cursor: permissionCount > 0 ? "pointer" : "default", textDecoration: permissionCount > 0 ? "underline dotted" : "none" }}
        >
          {permissionCount > 0 ? `View ${permissionCount} Permissions` : "No permissions"}
        </div>
        <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
          <button onClick={(e) => { e.stopPropagation(); onEdit(node.menuId); }} style={{ padding: "4px 8px", borderRadius: "5px", border: `1px solid ${COLORS.border}`, background: "#fff", color: COLORS.text, cursor: "pointer", fontSize: "10px", fontWeight: "600" }}>EDIT</button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(node.menuId); }} style={{ padding: "4px 8px", borderRadius: "5px", border: `1px solid ${COLORS.danger}33`, background: "#fff", color: COLORS.danger, cursor: "pointer", fontSize: "10px", fontWeight: "600" }}>DEACTIVATE</button>
        </div>
      </div>
      {isOpen && hasChildren && node.children.map(child => (
        <TreeRow key={child.menuId} node={child} onEdit={onEdit} onDelete={onDelete} onShowPerms={onShowPerms} activeId={activeId} openIds={openIds} onToggle={onToggle} level={level + 1} availablePermissions={availablePermissions} allMenus={allMenus} />
      ))}
    </>
  );
};

const MobileMenuCard = ({ node, onEdit, onDelete, onShowPerms, allMenus = [], level = 0 }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const hasChildren = node.children && node.children.length > 0;
  const permissionCount = node.permissionIds?.length || 0;

  const parentMenu = allMenus.find(m => m.menuId === node.parentId);
  const parentName = parentMenu ? parentMenu.menuName : (node.parentId === 0 ? "—" : "—");

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: '10px',
      padding: '14px',
      marginBottom: '10px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
          {hasChildren && (
            <button
              onClick={() => setIsOpen(!isOpen)}
              style={{ background: 'none', border: 'none', padding: '2px', cursor: 'pointer', color: COLORS.textMuted, fontSize: '13px' }}
            >
              {isOpen ? '▼' : '▶'}
            </button>
          )}
          <div 
            style={{ fontSize: '14.5px', fontWeight: '600', color: COLORS.accent, cursor: 'pointer', flex: 1 }}
            onClick={() => onEdit(node.menuId)}
          >
            {node.menuName}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '6px' }}>
          <button 
            onClick={() => onEdit(node.menuId)} 
            style={{ 
              padding: window.innerWidth < 768 ? '6px' : '5px 10px', 
              fontSize: '11px', 
              borderRadius: '6px', 
              border: `1px solid ${COLORS.border}`,
              width: window.innerWidth < 768 ? '36px' : 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Edit"
          >
            {window.innerWidth < 768 ? <Edit2 size={15} /> : 'Edit'}
          </button>

          <button 
            onClick={() => onDelete(node.menuId)} 
            style={{ 
              padding: window.innerWidth < 768 ? '6px' : '5px 10px', 
              fontSize: '11px', borderRadius: '6px', 
              border: `1px solid ${COLORS.danger}44`, 
              color: COLORS.danger, justifyContent: 'center',
              width: window.innerWidth < 768 ? '42px' : 'auto',
              height: window.innerWidth < 768 ? '36px' : 'auto',
              display: 'flex', alignItems: 'center'              
            }}
            title={node.isActive ? "Deactivate" : "Activate"}
          >
            {window.innerWidth < 768 ? (
              node.isActive ? 
                <FaToggleOn size={20} color="#0aa375" /> : 
                <FaToggleOff size={20} color="#94a3b8" />
            ) : (
              node.isActive ? 'Deactivate' : 'Activate'
            )}
          </button>
        </div>
      </div>

      <div style={{ marginTop: '10px', fontSize: '13px', lineHeight: '1.6', color: COLORS.text }}>
        <div><strong>DISPLAY:</strong> {node.displayName}</div>
        <div><strong>PARENT:</strong> {parentName}</div>
        <div><strong>ROUTING:</strong> {node.routingPath || "—"}</div>
        <div 
          onClick={() => permissionCount > 0 && onShowPerms(node)}
          style={{ 
            color: permissionCount > 0 ? COLORS.accent : '#94a3b8', 
            textDecoration: permissionCount > 0 ? 'underline' : 'none',
            cursor: permissionCount > 0 ? 'pointer' : 'default',
            marginTop: '4px'
          }}
        >
          {permissionCount > 0 ? `View ${permissionCount} Permissions` : "No permissions"}
        </div>
      </div>

      {isOpen && hasChildren && (
        <div style={{ marginTop: '12px', paddingLeft: '24px', borderLeft: `2px solid ${COLORS.accentMuted}` }}>
          {node.children.map(child => (
            <MobileMenuCard 
              key={child.menuId} 
              node={child} 
              onEdit={onEdit} 
              onDelete={onDelete} 
              onShowPerms={onShowPerms}
              allMenus={allMenus}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default function MenuMaster() {
  const [form, setForm] = useState({ menuName: "", displayName: "", parentId: 0, routingPath: "", seq: 1, isActive: true });
  const [selectedPerms, setSelectedPerms] = useState(new Set());
  const [rawMenus, setRawMenus] = useState([]);
  const [tableMenus, setTableMenus] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [openIds, setOpenIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [parentOptions, setParentOptions] = useState([]);
  const [deleteConfirmNode, setDeleteConfirmNode] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [viewingPermsNode, setViewingPermsNode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchMenuName, setSearchMenuName] = useState("");
  const [searchDisplayName, setSearchDisplayName] = useState("");
  const [searchParentName, setSearchParentName] = useState("");
  const [appliedSearch, setAppliedSearch] = useState({ menuName: "", displayName: "", parentName: "" });
  const [showFormModal, setShowFormModal] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  const API_BASE = sessionStorage.getItem("API_BASE_URL");

  const triggerToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const Spinner = () => (
    <div className="text-center py-4">
      <div className="spinner-border spinner-border-sm" style={{ color: BRAND_GREEN }} role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [menus, perms, allMaster] = await Promise.all([getMenu(API_BASE), getPermissions(API_BASE), getAllMenuMaster(API_BASE)]);
      if (menus) { setRawMenus(menus); setParentOptions([{ value: "0", label: "Root Level (No Parent)" }, ...menus.map(m => ({ value: String(m.menuId), label: m.menuName }))]); }
      if (allMaster) setTableMenus(allMaster);
      if (perms) setAvailablePermissions(perms);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [API_BASE]);

  useEffect(() => { loadData(); }, [loadData]);
  const filteredTableMenus = useMemo(() => {
    const { menuName, displayName, parentName } = appliedSearch;
    if (!menuName && !displayName && !parentName) return tableMenus;

    return tableMenus.filter(menu => {
      const parent = tableMenus.find(m => m.menuId === menu.parentId);
      const parentMenuName = parent ? parent.menuName.toLowerCase() : "";

      const matchMenuName = !menuName || menu.menuName.toLowerCase().includes(menuName.toLowerCase());
      const matchDisplayName = !displayName || menu.displayName.toLowerCase().includes(displayName.toLowerCase());
      const matchParentName = !parentName || parentMenuName.includes(parentName.toLowerCase());

      return matchMenuName && matchDisplayName && matchParentName;
    });
  }, [tableMenus, appliedSearch]);

  const menuTree = useMemo(() => buildMenuTree(filteredTableMenus), [filteredTableMenus]);

  const isSearchActive = appliedSearch.menuName || appliedSearch.displayName || appliedSearch.parentName;

  const handleSearch = () => {
    setAppliedSearch({ menuName: searchMenuName, displayName: searchDisplayName, parentName: searchParentName });
  };

  const handleReset = () => {
    setSearchMenuName("");
    setSearchDisplayName("");
    setSearchParentName("");
    setAppliedSearch({ menuName: "", displayName: "", parentName: "" });
  };

  const handleEditSelect = async (id) => {
    const data = await getMenuById(id);
    if (data) {
      setEditingId(data.menuId);
      setForm({ ...data, parentId: data.parentId || 0 });
      setSelectedPerms(new Set(data.permissionIds || []));
      setShowFormModal(true); 
    }
  };

const handleAddNew = () => {
  setEditingId(null);
  const nextSeq = getNextSeq(tableMenus, 0); // default parentId = 0 (root level)
  setForm({ menuName: "", displayName: "", parentId: 0, routingPath: "", seq: nextSeq, isActive: true });
  setSelectedPerms(new Set());
  setShowFormModal(true);
};

  const handleModalClose = () => {
    setShowFormModal(false);
    setEditingId(null);
    setForm({ menuName: "", displayName: "", parentId: 0, routingPath: "", seq: 1, isActive: true });
    setSelectedPerms(new Set());
  };

  const handleSave = async () => {
    if (!form.menuName || !form.displayName) return triggerToast("Required fields missing", "warning");
    setSaving(true);
    try {
      const payload = { ...form, parentId: parseInt(form.parentId) || 0, permissionIds: Array.from(selectedPerms).map(Number) };
      editingId ? await updateMenu(editingId, payload) : await createMenu(payload);
      handleModalClose();
      clearMenuCache();
      await loadData();
      triggerToast(`Menu ${editingId ? "updated" : "created"} successfully!`, "success");
      setTimeout(() => window.location.reload(), 800);
    } catch { triggerToast("Error saving menu", "error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteConfirmNode) return;
    try {
      const data = await getMenuById(deleteConfirmNode.menuId);
      if (data) { await updateMenu(deleteConfirmNode.menuId, { ...data, isActive: false }); clearMenuCache(); await loadData(); }
    } catch (err) { console.error(err); }
    finally { setDeleteConfirmNode(null); }
  };

  const inp = { padding: "5px 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px", boxSizing: "border-box", background: "#fff", width: "100%" };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "0px 0px", boxSizing: "border-box", gap: "8px" }}>
      <style>{styles}</style>
      <MenuFormModal
        show={showFormModal}
        onClose={handleModalClose}
        onSave={handleSave}
        saving={saving}
        form={form}
        setForm={setForm}
        selectedPerms={selectedPerms}
        setSelectedPerms={setSelectedPerms}
        availablePermissions={availablePermissions}
        parentOptions={parentOptions}
        editingId={editingId}
        tableMenus={tableMenus}
      />
      {viewingPermsNode && (
        <div className="modal-overlay" onClick={() => setViewingPermsNode(null)}>
          <div className="glass-card" onClick={e => e.stopPropagation()} style={{ width: "300px", padding: "16px" }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: `1px solid ${COLORS.border}`, paddingBottom: '8px' }}>
              <span style={{ fontWeight: '700', fontSize: '13px', color: COLORS.accent }}>{viewingPermsNode.menuName} Permissions</span>
              <button onClick={() => setViewingPermsNode(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: COLORS.textMuted }}>×</button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {viewingPermsNode.permissionIds.map(id => {
                const p = availablePermissions.find(perm => perm.permissionId === id);
                return p ? <span key={id} className="perm-badge">{p.name}</span> : null;
              })}
            </div>
          </div>
        </div>
      )}
      {deleteConfirmNode && (
        <div className="modal-overlay" onClick={() => setDeleteConfirmNode(null)}>
          <div className="glass-card" onClick={e => e.stopPropagation()} style={{ width: "340px", padding: "22px" }}>
            <div style={{ textAlign: "center", marginBottom: "16px" }}>
              <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#fff1f2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", margin: "0 auto 10px" }}>🗑️</div>
              <div style={{ fontWeight: "700", fontSize: "14px", color: COLORS.text, marginBottom: "4px" }}>Deactivate Menu</div>
              <div style={{ fontSize: "12px", color: COLORS.textMuted }}>Deactivate <strong>"{deleteConfirmNode.menuName}"</strong>?</div>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button onClick={() => setDeleteConfirmNode(null)} style={{ flex: 1, padding: "8px", borderRadius: "7px", border: `1px solid ${COLORS.border}`, background: "#fff", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>Cancel</button>
              <button onClick={handleDelete} style={{ flex: 1, padding: "8px", borderRadius: "7px", border: "none", background: COLORS.danger, fontSize: "12px", fontWeight: "600", cursor: "pointer", color: "#fff" }}>Yes, Deactivate</button>
            </div>
          </div>
        </div>
      )}
      <div className="glass-card" style={{ padding: '12px 14px' }}>
      {showFilters && (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 160px', minWidth: '140px' }}>
            <div style={{ fontSize: '10px', fontWeight: '600', color: COLORS.textMuted, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Menu Name</div>
            <input
              type="text"
              value={searchMenuName}
              onChange={e => setSearchMenuName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
              placeholder="Search menu name..."
              style={inp}
            />
          </div>
          <div style={{ flex: '1 1 160px', minWidth: '140px' }}>
            <div style={{ fontSize: '10px', fontWeight: '600', color: COLORS.textMuted, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Display Name</div>
            <input
              type="text"
              value={searchDisplayName}
              onChange={e => setSearchDisplayName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
              placeholder="Search display name..."
              style={inp}
            />
          </div>
          <div style={{ flex: '1 1 160px', minWidth: '140px' }}>
            <div style={{ fontSize: '10px', fontWeight: '600', color: COLORS.textMuted, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Parent Name</div>
            <input
              type="text"
              value={searchParentName}
              onChange={e => setSearchParentName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
              placeholder="Search parent name..."
              style={inp}
            />
          </div>

          <div style={{ display: 'flex', gap: '6px', alignSelf: 'flex-end', flexWrap: 'wrap' }}>
            <button 
              onClick={handleSearch} 
              className="btn-primary" 
              style={{ 
                padding: window.innerWidth < 768 ? '6px' : '6px 14px', 
                width: window.innerWidth < 768 ? '36px' : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Search"
            >
              {window.innerWidth < 768 ? <Search size={16} /> : 'Search'}
            </button>

            <button 
              onClick={handleReset} 
              style={{ 
                padding: window.innerWidth < 768 ? '6px' : '6px 14px', 
                borderRadius: '7px', 
                border: `1px solid ${COLORS.border}`, 
                background: '#fff', 
                fontSize: '12px', 
                fontWeight: '600', 
                cursor: 'pointer', 
                width: window.innerWidth < 768 ? '36px' : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Reset"
            >
              {window.innerWidth < 768 ? <RotateCcw size={16} /> : 'Reset'}
            </button>

            <button 
              onClick={handleAddNew} 
              className="btn-primary" 
              style={{ 
                padding: window.innerWidth < 768 ? '6px' : '6px 16px', 
                whiteSpace: 'nowrap', 
                display: 'flex', 
                alignItems: 'center', 
                gap: window.innerWidth < 768 ? '0' : '6px',
                width: window.innerWidth < 768 ? '36px' : 'auto',
                justifyContent: 'center'
              }}
              title="Add Menu"
            >
              {window.innerWidth < 768 ? <Plus size={16} /> : <><span style={{ fontSize: '16px', lineHeight: 1 }}>+</span> Add Menu</>}
            </button>
          </div>

          <button
            className="d-md-none"
            onClick={() => setShowFilters(false)}
            style={{ 
              padding: '6px 12px', 
              borderRadius: '7px', 
              border: `1px solid ${COLORS.border}`, 
              background: '#fff', 
              fontSize: '11px', 
              fontWeight: '600', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              marginLeft: 'auto'
            }}
          >
            Hide Filters<ChevronUp size={14} />
          </button>
        </div>
      )}

      <div className="d-flex justify-content-end align-items-center mt-2 pt-2 border-top">
        {!showFilters && (
          <button
            className="btn btn-sm btn-outline-secondary d-md-none d-flex align-items-center gap-1"
            style={{ fontSize: '12px', height: '28px' }}
            onClick={() => setShowFilters(true)}
          >
            Show Filters <ChevronDown size={14} />
          </button>
        )}
      </div>
    </div>
      <div className="glass-card" style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
        <div className="d-none d-md-grid" style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 0.8fr 1fr 1.8fr 110px", padding: "5px 16px", background: COLORS.headerBg, fontSize: "10.5px", fontWeight: "700", color: COLORS.textMuted, letterSpacing: '0.5px', borderBottom: `1px solid ${COLORS.border}` }}>
          <div>MENU NAME</div><div>DISPLAY</div><div>PARENT</div><div>ROUTING</div><div>PERMISSIONS</div><div style={{ textAlign: "center" }}>ACTION</div>
        </div>
        <div style={{ flex: 1, overflowY: "auto" }} className="custom-scroll">
          {loading ? (
            <div style={{ padding: "40px", textAlign: "center" }}>
              <Spinner />
              <div style={{ color: COLORS.textMuted, fontSize: "12px", marginTop: "8px" }}>Loading menus...</div>
            </div>
          ) : menuTree.length > 0 ? (
            <>
              <div className="d-none d-md-block">
                {menuTree.map(root => (
                  <TreeRow
                    key={root.menuId}
                    node={root}
                    onEdit={handleEditSelect}
                    onDelete={(id) => { const node = tableMenus.find(m => m.menuId === id); if (node) setDeleteConfirmNode(node); }}
                    onShowPerms={setViewingPermsNode}
                    activeId={editingId}
                    openIds={openIds}
                    onToggle={(id) => setOpenIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; })}
                    availablePermissions={availablePermissions}
                    allMenus={tableMenus}
                  />
                ))}
              </div>

              {/* Mobile Card View */}
              <div className="d-md-none" style={{ padding: '0 8px' }}>
                {menuTree.map(root => (
                  <MobileMenuCard 
                    key={root.menuId} 
                    node={root} 
                    onEdit={handleEditSelect} 
                    onDelete={(id) => { const node = tableMenus.find(m => m.menuId === id); if (node) setDeleteConfirmNode(node); }}
                    onShowPerms={setViewingPermsNode}
                    allMenus={tableMenus}
                  />
                ))}
              </div>
            </>
          ) : (
            <div style={{ padding: "40px", textAlign: "center", color: COLORS.textMuted }}>
              {isSearchActive ? "No menus match your search criteria." : "No menu data available."}
            </div>
          )}
        </div>
      </div>
      <Toast message={toast.message} type={toast.type} isVisible={toast.show} />
    </div>
  );
}